import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useRequestReset, useResetPassword } from './useOnboarding'

// „Passwort vergessen?" — E-Mail-basierter Self-Service-Reset (Admins / Nutzer mit hinterlegter E-Mail).
//  • ohne ?token: Anforderungsformular (E-Mail/Benutzername) → POST /request-reset (Antwort immer neutral).
//  • mit  ?token (aus dem gemailten Link): neues Passwort setzen → POST /reset-password.
// Mitarbeiter ohne E-Mail nutzen stattdessen den Chef-Code (/passwort-zuruecksetzen) — als Querverweis verlinkt.
export function ForgotPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  return token ? <SetNewPassword token={token} /> : <RequestReset />
}

function RequestReset() {
  const request = useRequestReset()
  const [login, setLogin] = useState('')

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    request.mutate({ login: login.trim() })
  }

  if (request.isSuccess) {
    return (
      <div className="centered">
        <div className="card login">
          <h1>E-Mail unterwegs</h1>
          <p className="hint">
            Falls ein Konto mit hinterlegter E-Mail existiert, haben wir dir einen Link zum Zurücksetzen geschickt
            (15 Minuten gültig). Sieh auch im Spam-Ordner nach.
          </p>
          <Link to="/login" className="btn">
            Zur Anmeldung
          </Link>
        </div>
      </div>
    )
  }

  const errorMessage = request.error instanceof ApiError ? request.error.message : request.isError ? 'Anfrage fehlgeschlagen' : null

  return (
    <div className="centered">
      <form className="card login" onSubmit={onSubmit}>
        <h1>Passwort vergessen</h1>
        <p className="hint">Gib deine E-Mail oder deinen Benutzernamen ein — wir schicken dir einen Reset-Link.</p>
        <label className="field">
          <span>E-Mail oder Benutzername</span>
          <input
            name="login"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />
        </label>
        {errorMessage && (
          <p className="error" role="alert">
            {errorMessage}
          </p>
        )}
        <button type="submit" className="btn" disabled={request.isPending}>
          {request.isPending ? 'Wird gesendet…' : 'Reset-Link anfordern'}
        </button>
        <nav className="wf-authlinks" aria-label="Alternativen">
          <Link to="/passwort-zuruecksetzen">Kein E-Mail-Konto? Code vom Chef einlösen</Link>
          <Link to="/login">Zurück zur Anmeldung</Link>
        </nav>
      </form>
    </div>
  )
}

function SetNewPassword({ token }: { token: string }) {
  const reset = useResetPassword()
  const [password, setPassword] = useState('')

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    reset.mutate({ token, password })
  }

  if (reset.isSuccess) {
    return (
      <div className="centered">
        <div className="card login">
          <h1>Passwort geändert</h1>
          <p className="hint">Dein Passwort wurde gesetzt. Du kannst dich jetzt mit dem neuen Passwort anmelden.</p>
          <Link to="/login" className="btn">
            Zur Anmeldung
          </Link>
        </div>
      </div>
    )
  }

  const errorMessage = reset.error instanceof ApiError ? reset.error.message : reset.isError ? 'Zurücksetzen fehlgeschlagen' : null

  return (
    <div className="centered">
      <form className="card login" onSubmit={onSubmit}>
        <h1>Neues Passwort setzen</h1>
        <p className="hint">Vergib ein neues Passwort für dein Konto.</p>
        <label className="field">
          <span>Neues Passwort (min. 8 Zeichen)</span>
          <input name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {errorMessage && (
          <p className="error" role="alert">
            {errorMessage}
          </p>
        )}
        <button type="submit" className="btn" disabled={reset.isPending}>
          {reset.isPending ? 'Wird gesetzt…' : 'Passwort setzen'}
        </button>
        <nav className="wf-legalfoot" aria-label="Navigation">
          <Link to="/login">Zurück zur Anmeldung</Link>
        </nav>
      </form>
    </div>
  )
}
